<script setup>
  import { computed, onMounted, onBeforeUnmount, ref } from "vue";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { storeToRefs } from "pinia";
  
  import { Card } from "@/components/ui/card";
  import AiInsightMessageList from "@/components/Survey/Assistant/AiInsightMessageList.vue";
  import AiInsightMessageEdit from "@/components/Survey/Assistant/AiInsightMessageEdit.vue";
  import SurveyContextBanner from "@/components/Survey/Context/SurveyContextBanner.vue";
  import { useAiInsightConversationsStore } from "@stores/survey/assistant/aiInsightConversations";
  import { useAiInsightConversationMessagesStore } from "@stores/survey/assistant/aiInsightConversationMessages";
  import { useSurveyStore } from "@stores/survey/survey";

  const { selectedConversation } = storeToRefs(useAiInsightConversationsStore());
  const { conversationMessages } = storeToRefs(useAiInsightConversationMessagesStore());
  const { isLiveOrComplete } = storeToRefs(useSurveyStore());

  const isEmpty = computed(() => !conversationMessages.value || conversationMessages.value.length === 0);

  // Empty-state suggestions: a single example rotates in/out under the title
  // (decorative, not clickable). Kept in English to match the rest of the UI.
  const suggestedPrompts = [
    "What are the main themes behind respondents' frustrations?",
    "How do answers differ between younger and older segments?",
    "Find verbatims that mention price or value for money.",
    "How many respondents would recommend us to a friend?",
  ];
  const rotatingPromptIndex = ref(0);
  let rotatingTimer = null;

  onMounted(() => {
    rotatingTimer = setInterval(() => {
      rotatingPromptIndex.value = (rotatingPromptIndex.value + 1) % suggestedPrompts.length;
    }, 2800);
  });

  onBeforeUnmount(() => clearInterval(rotatingTimer));
</script>

<template>
  <Card class="flex h-[calc(100vh-60px-40px)] flex-col overflow-hidden p-0">
    <!-- Header (hidden while the survey is still collecting data) -->
    <div
      v-if="isLiveOrComplete"
      class="flex shrink-0 items-center border-b px-6 py-3">
      <h2
        class="truncate text-sm font-semibold"
        data-cy="assistant-thread-title">
        {{ isEmpty ? "New chat" : selectedConversation.title }}
      </h2>
    </div>

    <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
      <!-- Draft: data collection hasn't started yet -->
      <div
        v-if="!isLiveOrComplete"
        class="flex flex-1 flex-col items-center justify-center px-8 text-center">
        <div class="mb-5 flex items-center gap-2.5">
          <AiStar class="h-7 w-7 shrink-0 text-primary" />
          <h1 class="text-2xl font-semibold tracking-tight">AI Insights</h1>
        </div>
        <p class="max-w-[460px] text-sm text-muted-foreground">
          Generate insights on your survey's results once the data collection has started.
        </p>
      </div>

      <!-- Empty state: new thread, ListenLabs-style rolling suggestion -->
      <div
        v-else-if="isEmpty"
        class="-mt-8 flex flex-1 flex-col items-center justify-center px-8">
        <div class="mb-5 flex items-center gap-2.5">
          <AiStar class="h-7 w-7 shrink-0 text-primary" />
          <div class="text-2xl font-semibold tracking-tight text-foreground">What do you want to explore?</div>
        </div>
        <div class="relative h-6 w-full max-w-[560px] overflow-hidden">
          <Transition name="rollup">
            <p
              :key="rotatingPromptIndex"
              class="absolute inset-x-0 truncate px-4 text-center text-sm italic text-muted-foreground/70">
              {{ suggestedPrompts[rotatingPromptIndex] }}
            </p>
          </Transition>
        </div>
      </div>

      <!-- Conversation -->
      <AiInsightMessageList v-else />
    </div>

    <!-- Composer (only once the survey is collecting data) -->
    <div
      v-if="isLiveOrComplete"
      class="shrink-0 px-6 pb-5 pt-3">
      <SurveyContextBanner
        v-if="isEmpty"
        class="mx-auto mb-2.5 max-w-[760px]" />
      <AiInsightMessageEdit />
    </div>
  </Card>
</template>

<style scoped>
  /* Vertical roll for the empty-state suggestion carousel: the next example rises
     from below while the current one exits upward. */
  .rollup-enter-active,
  .rollup-leave-active {
    transition:
      transform 0.55s cubic-bezier(0.22, 1, 0.36, 1),
      opacity 0.55s ease;
  }
  .rollup-enter-from {
    transform: translateY(100%);
    opacity: 0;
  }
  .rollup-leave-to {
    transform: translateY(-100%);
    opacity: 0;
  }
</style>
